// Arab-country detection + welcome-screen gating.
//
// Zero-network, zero-permission: reads the browser's IANA timezone
// (`Intl.DateTimeFormat().resolvedOptions().timeZone`). Arab timezones
// map 1:1 to country codes, so a device set to Africa/Cairo is an
// Egyptian user, Asia/Amman is Jordan, etc. Users who VPN or change
// their timezone won't be shown the screen — that's acceptable for a
// marketing surface (it's a nice-to-have, not a security gate).
//
// Cached to localStorage so we only detect once per install.
//
// Countries supported (expanded Sep 30, 2026 to full MENA-Arab batch):
//   Gulf:   SA, AE, QA, KW, OM, BH
//   Levant: JO, IQ
//   North Africa: EG, MA, TN, DZ
//
// Historical note: function names are prefixed `Gulf` for backwards
// compatibility with import sites — the actual detection matrix now
// covers 12 Arab countries. Do not rename without a codebase-wide grep.

const CACHE_KEY = "calofay_country_v1";
const SHOWN_KEY = "calofay_country_welcome_shown_v1";

const TZ_TO_COUNTRY = {
  // Gulf
  "Asia/Riyadh": "SA",
  "Asia/Kuwait": "KW",
  "Asia/Qatar": "QA",
  "Asia/Bahrain": "BH",
  "Asia/Dubai": "AE",
  "Asia/Abu_Dhabi": "AE",
  "Asia/Muscat": "OM",
  // Levant
  "Asia/Amman": "JO",
  "Asia/Baghdad": "IQ",
  // North Africa
  "Africa/Cairo": "EG",
  "Africa/Casablanca": "MA",
  "Africa/El_Aaiun": "MA",
  "Africa/Tunis": "TN",
  "Africa/Algiers": "DZ",
};

// Country metadata used by CountryWelcome.jsx. Kept in this module so
// the component stays presentation-only and this file is the single
// source of truth for "who is in the launch batch".
//
// Arabic naming grammar: every entry uses the "في + <country name with
// article>" pattern (e.g., "في السعودية"). Verified by a native
// speaker — no country name here reads unnaturally.
export const GULF_COUNTRIES = {
  SA: { code: "SA", flag: "🇸🇦", name_ar: "السعودية", name_en: "Saudi Arabia",         in_ar: "في السعودية",   in_en: "in Saudi Arabia" },
  AE: { code: "AE", flag: "🇦🇪", name_ar: "الإمارات",  name_en: "United Arab Emirates", in_ar: "في الإمارات",   in_en: "in the UAE"       },
  QA: { code: "QA", flag: "🇶🇦", name_ar: "قطر",       name_en: "Qatar",                in_ar: "في قطر",        in_en: "in Qatar"         },
  KW: { code: "KW", flag: "🇰🇼", name_ar: "الكويت",    name_en: "Kuwait",               in_ar: "في الكويت",     in_en: "in Kuwait"        },
  OM: { code: "OM", flag: "🇴🇲", name_ar: "عُمان",     name_en: "Oman",                 in_ar: "في عُمان",       in_en: "in Oman"          },
  BH: { code: "BH", flag: "🇧🇭", name_ar: "البحرين",   name_en: "Bahrain",              in_ar: "في البحرين",    in_en: "in Bahrain"       },
  EG: { code: "EG", flag: "🇪🇬", name_ar: "مصر",       name_en: "Egypt",                in_ar: "في مصر",        in_en: "in Egypt"         },
  JO: { code: "JO", flag: "🇯🇴", name_ar: "الأردن",    name_en: "Jordan",               in_ar: "في الأردن",     in_en: "in Jordan"        },
  IQ: { code: "IQ", flag: "🇮🇶", name_ar: "العراق",    name_en: "Iraq",                 in_ar: "في العراق",     in_en: "in Iraq"          },
  MA: { code: "MA", flag: "🇲🇦", name_ar: "المغرب",    name_en: "Morocco",              in_ar: "في المغرب",     in_en: "in Morocco"       },
  TN: { code: "TN", flag: "🇹🇳", name_ar: "تونس",      name_en: "Tunisia",              in_ar: "في تونس",       in_en: "in Tunisia"       },
  DZ: { code: "DZ", flag: "🇩🇿", name_ar: "الجزائر",   name_en: "Algeria",              in_ar: "في الجزائر",    in_en: "in Algeria"       },
};

/** Returns a 2-letter Arab country code or null. Cached. */
export function detectGulfCountry() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached !== null) return cached || null;
  } catch {}

  let cc = null;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    cc = TZ_TO_COUNTRY[tz] || null;
  } catch {}

  try { localStorage.setItem(CACHE_KEY, cc || ""); } catch {}
  return cc;
}

/** Full country meta object, or null if outside launch batch. */
export function getGulfCountryMeta() {
  const cc = detectGulfCountry();
  return cc ? GULF_COUNTRIES[cc] || null : null;
}

/** True if we have NOT yet shown the welcome screen to this install. */
export function shouldShowCountryWelcome() {
  const meta = getGulfCountryMeta();
  if (!meta) return false;
  try {
    return localStorage.getItem(SHOWN_KEY) !== "1";
  } catch {
    return true;
  }
}

/** Called after the user taps "Continue" so we don't show it again. */
export function markCountryWelcomeShown() {
  try { localStorage.setItem(SHOWN_KEY, "1"); } catch {}
}

/** Test / dev helper — clears the "already shown" flag so a QA can
 *  re-verify the screen without wiping the whole localStorage. */
export function resetCountryWelcome() {
  try {
    localStorage.removeItem(SHOWN_KEY);
    localStorage.removeItem(CACHE_KEY);
  } catch {}
}
